import React from 'react';
import { useTeamTab } from '../../TeamTabContext';

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

export const CapacityZonePanel: React.FC = () => {
  const {
    estimationUnitsSectionRef,
    highlightEstimationUnitsSection,
    projectSettings,
    setProjectSettings,
    loadingProjectSettings,
    savingProjectSettings,
    projectSettingsSaved,
    projectSettingsSaveError,
    saveProjectSettings,
  } = useTeamTab();

  return (
    <>
      {projectSettingsSaveError && (
        <div className="settings-callout settings-callout--danger" style={{ marginBottom: '16px' }}>
          <div className="settings-callout__body">{projectSettingsSaveError}</div>
        </div>
      )}

      {/* Estimation units (for Capacity & Forecast) */}
      <div
        ref={estimationUnitsSectionRef as React.Ref<HTMLDivElement>}
        id="settings-estimation-units-section"
        className={`settings-panel${highlightEstimationUnitsSection ? ' forecast-tour-highlight' : ''}`}
        style={highlightEstimationUnitsSection ? { borderRadius: '16px' } : undefined}
      >
        <div className="settings-panel__header">
          <h3 className="settings-panel__title">Estimation units (for Capacity &amp; Forecast)</h3>
          <p className="settings-panel__description">
            Choose how effort is expressed for capacity and forecast views. Used to convert Jira
            estimates into days and to show &quot;1 day = X hours&quot; in Forecast.
          </p>
        </div>

        {loadingProjectSettings ? (
          <div
            style={{
              padding: '8px 0',
              color: 'var(--color-text-secondary)',
              fontSize: '13px',
            }}
          >
            Loading...
          </div>
        ) : (
          <div className="settings-toolbar">
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '13px',
                  fontWeight: '500',
                  color: 'var(--color-text-secondary)',
                  cursor: 'pointer',
                }}
              >
                <input
                  type="radio"
                  name="estimationUnit"
                  checked={projectSettings.estimationUnit === 'hours_per_day'}
                  onChange={() =>
                    setProjectSettings((prev) => ({
                      ...prev,
                      estimationUnit: 'hours_per_day',
                    }))
                  }
                  style={{ width: '16px', height: '16px', accentColor: '#6366f1' }}
                />
                Hours per day
              </label>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '13px',
                  color: 'var(--color-text-secondary)',
                  cursor: 'not-allowed',
                }}
                title="Coming soon"
              >
                <input
                  type="radio"
                  name="estimationUnit"
                  checked={projectSettings.estimationUnit === 'story_points_per_day'}
                  disabled
                  readOnly
                  style={{ width: '16px', height: '16px' }}
                />
                Story points per day <span style={{ fontSize: '11px' }}>(coming soon)</span>
              </label>
            </div>
            {projectSettings.estimationUnit === 'hours_per_day' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <label
                  style={{
                    fontSize: '12px',
                    fontWeight: '500',
                    color: 'var(--color-text-secondary)',
                  }}
                >
                  Hours per day:
                </label>
                <input
                  type="number"
                  className="form-input"
                  min={0.5}
                  max={24}
                  step={0.5}
                  value={projectSettings.estimationHoursPerDay}
                  onChange={(e) =>
                    setProjectSettings((prev) => ({
                      ...prev,
                      estimationHoursPerDay: parseFloat(e.target.value) || 8,
                    }))
                  }
                  style={{ width: '80px', minHeight: '36px', textAlign: 'right' }}
                />
              </div>
            )}
            <button
              type="button"
              onClick={saveProjectSettings}
              disabled={savingProjectSettings}
              style={{
                padding: '8px 16px',
                backgroundColor: savingProjectSettings ? '#9ca3af' : '#6366f1',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: '500',
                cursor: savingProjectSettings ? 'not-allowed' : 'pointer',
              }}
            >
              {savingProjectSettings ? 'Saving...' : 'Save'}
            </button>
          </div>
        )}
        {projectSettingsSaved && (
          <div className="success-message" role="status" style={{ marginTop: 12 }}>
            <span className="success-icon">{'\u2713'}</span>
            Project settings saved successfully!
          </div>
        )}
      </div>

      {/* Work Schedule */}
      {!loadingProjectSettings && (
        <div className="settings-panel">
          <div className="settings-panel__header">
            <h3 className="settings-panel__title">Work Schedule</h3>
            <p className="settings-panel__description">
              Select which days are working days. Used for capacity and forecast calculations. At
              least one day must be selected.
            </p>
          </div>
          <div className="settings-panel__body">
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {DAY_LABELS.map((label, idx) => {
                const isActive = projectSettings.workingDays.includes(idx);
                const isOnlyOne = isActive && projectSettings.workingDays.length === 1;
                return (
                  <button
                    key={idx}
                    type="button"
                    title={isOnlyOne ? 'At least one working day is required' : undefined}
                    onClick={() => {
                      if (isOnlyOne) return;
                      setProjectSettings((prev) => ({
                        ...prev,
                        workingDays: isActive
                          ? prev.workingDays.filter((d) => d !== idx)
                          : [...prev.workingDays, idx].sort((a, b) => a - b),
                      }));
                    }}
                    style={{
                      padding: '6px 14px',
                      borderRadius: '6px',
                      border: isActive
                        ? '2px solid #6366f1'
                        : '2px solid var(--profile-nested-border)',
                      backgroundColor: isActive ? '#6366f1' : 'var(--profile-nested-table-row-bg)',
                      color: isActive ? '#ffffff' : 'var(--color-text-secondary)',
                      fontSize: '13px',
                      fontWeight: '500',
                      cursor: isOnlyOne ? 'not-allowed' : 'pointer',
                      transition: 'all 0.15s ease',
                      opacity: isOnlyOne ? 0.6 : 1,
                    }}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
            <div className="settings-toolbar" style={{ marginTop: '12px' }}>
              <span style={{ fontSize: '12px', color: 'var(--color-text-secondary)' }}>
                {projectSettings.workingDays.length} working day
                {projectSettings.workingDays.length !== 1 ? 's' : ''} per week
              </span>
              <button
                type="button"
                onClick={saveProjectSettings}
                disabled={savingProjectSettings}
                style={{
                  padding: '8px 16px',
                  backgroundColor: savingProjectSettings ? '#9ca3af' : '#6366f1',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: savingProjectSettings ? 'not-allowed' : 'pointer',
                }}
              >
                {savingProjectSettings ? 'Saving...' : 'Save'}
              </button>
            </div>
            {projectSettingsSaved && (
              <div className="success-message" role="status" style={{ marginTop: 12 }}>
                <span className="success-icon">{'\u2713'}</span>
                Project settings saved successfully!
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};
